// WP-7.1 (M30 SM-1..4, LOCKED §2.35, S-33 `/simulator`): the scenario simulator — a sandbox
// that projects the balance line forward under a set of hypothetical operations, NEVER writing
// to real records (SM-2, enforced structurally by the engine's own pure `runScenario`). Running
// a scenario is instant/disposable (kept only in this page's own React state, up to 3 at once
// for comparison); saving one is a deliberate, separate action (SM-4).
import {
  formatMoney,
  fromMinor,
  parseAmountInput,
  type CurrencyCode,
  type ScenarioOperationKind,
} from '@pfos/engine';
import type {
  GoalResponse,
  RpResponse,
  SavedScenario,
  ScenarioOperation,
  ScenarioResultResponse,
} from '@pfos/shared';
import type { EChartsCoreOption } from 'echarts';
import { FlaskConical } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Amount } from '../components/Amount';
import { EChart } from '../components/EChart';
import { EmptyState } from '../components/EmptyState';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { useGoals } from '../lib/goals';
import { useRps } from '../lib/rps';
import {
  useDeleteScenario,
  useRunScenario,
  useSaveScenario,
  useSavedScenarios,
} from '../lib/simulator';

const CURRENCIES: readonly CurrencyCode[] = ['PKR', 'USD', 'EUR', 'GBP'];

const OPERATION_KIND_LABELS: Record<ScenarioOperationKind, string> = {
  delay: 'Delay a payment',
  change: 'Change an amount',
  add: 'Add an expense or income',
  payAll: 'Pay all debts now',
  solveSpend: 'Solve safe daily spend',
  solveSaving: 'Solve required saving',
};

export interface ScenarioSlot {
  key: string;
  label: string;
  result: ScenarioResultResponse;
  /** Which solve op (if any) produced `result.solvedAmountMinor` — labels the delta table row. */
  solveKind?: 'solveSpend' | 'solveSaving';
}

// 18.7-style fixed palette (mirrors FlowExplorer's own LEVEL_COLORS approach) — one color per
// comparison slot, capped at 3 (the page's own comparison-set limit).
const SLOT_COLORS = ['#2563eb', '#7c3aed', '#0891b2'];
const BREACH_COLOR = '#dc2626';
const RESERVE_FLOOR_COLOR = '#64748b';

/** Pure chart-data shaping (unit-tested directly, per house convention — FlowExplorer's own
 * `buildChartData`): one line series per comparison slot, a shared dashed reserve-floor
 * reference line, and a danger-colored scatter series per slot for its own breach points. */
export function buildSimulatorChartOption(slots: readonly ScenarioSlot[]): EChartsCoreOption {
  const lineSeries = slots.map((slot, i) => ({
    name: slot.label,
    type: 'line' as const,
    showSymbol: false,
    data: slot.result.points.map((p) => [p.date, p.balanceMinor]),
    lineStyle: { color: SLOT_COLORS[i % SLOT_COLORS.length] },
    itemStyle: { color: SLOT_COLORS[i % SLOT_COLORS.length] },
  }));

  const breachSeries = slots
    .filter((slot) => slot.result.breaches.length > 0)
    .map((slot) => ({
      name: `${slot.label} — breach`,
      type: 'scatter' as const,
      data: slot.result.breaches.map((b) => [b.date, b.balanceMinor]),
      itemStyle: { color: BREACH_COLOR },
      symbolSize: 10,
    }));

  const firstSlot = slots[0];
  const reserveFloorSeries =
    firstSlot === undefined
      ? []
      : [
          {
            name: 'Reserve floor',
            type: 'line' as const,
            showSymbol: false,
            lineStyle: { type: 'dashed' as const, color: RESERVE_FLOOR_COLOR },
            data: firstSlot.result.points.map((p) => [p.date, firstSlot.result.reserveFloorMinor]),
          },
        ];

  return {
    tooltip: { trigger: 'axis' },
    legend: {},
    xAxis: { type: 'time' as const },
    yAxis: { type: 'value' as const },
    series: [...lineSeries, ...reserveFloorSeries, ...breachSeries],
  };
}

function describeOperation(
  op: ScenarioOperation,
  currency: CurrencyCode,
  rpPartyById: Map<string, string>,
  goalNameById: Map<string, string>,
): string {
  switch (op.kind) {
    case 'delay':
      return `Delay "${rpPartyById.get(op.rpId) ?? op.rpId}" by ${String(op.days)} day${op.days === 1 ? '' : 's'}`;
    case 'change':
      return `Change "${rpPartyById.get(op.rpId) ?? op.rpId}" to ${formatMoney(fromMinor(currency, op.newAmountMinor))}`;
    case 'add':
      return `Add ${op.cashflowKind} of ${formatMoney(fromMinor(currency, op.amountMinor))} on ${new Date(op.at).toLocaleDateString()} — ${op.label}`;
    case 'payAll':
      return 'Pay every open payable now';
    case 'solveSpend':
      return 'Solve for the safe daily spend';
    case 'solveSaving':
      return `Solve required saving for "${goalNameById.get(op.goalId) ?? op.goalId}"`;
  }
}

function AddOperationForm({
  currency,
  rps,
  goals,
  hasSolveOp,
  onAdd,
}: {
  currency: CurrencyCode;
  rps: RpResponse[];
  goals: GoalResponse[];
  hasSolveOp: boolean;
  onAdd: (op: ScenarioOperation) => void;
}): React.JSX.Element {
  const [kind, setKind] = useState<ScenarioOperationKind | ''>('');
  const [rpId, setRpId] = useState('');
  const [days, setDays] = useState(1);
  const [newAmount, setNewAmount] = useState('');
  const [cashflowKind, setCashflowKind] = useState<'expense' | 'income'>('expense');
  const [addAmount, setAddAmount] = useState('');
  const [at, setAt] = useState('');
  const [label, setLabel] = useState('');
  const [goalId, setGoalId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resetFields = (): void => {
    setKind('');
    setRpId('');
    setDays(1);
    setNewAmount('');
    setCashflowKind('expense');
    setAddAmount('');
    setAt('');
    setLabel('');
    setGoalId('');
    setError(null);
  };

  const handleAdd = (): void => {
    if (kind === 'delay') {
      if (rpId === '') {
        setError('Choose a receivable/payable');
        return;
      }
      if (!Number.isInteger(days) || days <= 0) {
        setError('Enter a positive number of days');
        return;
      }
      onAdd({ kind: 'delay', rpId, days });
    } else if (kind === 'change') {
      if (rpId === '') {
        setError('Choose a receivable/payable');
        return;
      }
      const parsed = parseAmountInput(newAmount, { defaultCurrency: currency });
      if (!parsed.ok) {
        setError('Enter a valid new amount');
        return;
      }
      onAdd({ kind: 'change', rpId, newAmountMinor: parsed.money.minorUnits });
    } else if (kind === 'add') {
      const parsed = parseAmountInput(addAmount, { defaultCurrency: currency });
      if (!parsed.ok) {
        setError('Enter a valid amount');
        return;
      }
      if (at === '') {
        setError('Choose a date');
        return;
      }
      if (label.trim() === '') {
        setError('Enter a label');
        return;
      }
      onAdd({
        kind: 'add',
        cashflowKind,
        amountMinor: parsed.money.minorUnits,
        at: new Date(at).toISOString(),
        label: label.trim(),
      });
    } else if (kind === 'payAll') {
      onAdd({ kind: 'payAll' });
    } else if (kind === 'solveSpend') {
      if (hasSolveOp) {
        setError(
          'Only one of solve safe spend / solve required saving may be included per scenario',
        );
        return;
      }
      onAdd({ kind: 'solveSpend' });
    } else if (kind === 'solveSaving') {
      if (hasSolveOp) {
        setError(
          'Only one of solve safe spend / solve required saving may be included per scenario',
        );
        return;
      }
      if (goalId === '') {
        setError('Choose a goal');
        return;
      }
      onAdd({ kind: 'solveSaving', goalId });
    } else {
      return;
    }
    resetFields();
  };

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <Field label="+ Add operation" htmlFor="sim-op-kind">
        <select
          id="sim-op-kind"
          className={selectClass}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as ScenarioOperationKind | '');
            setError(null);
          }}
        >
          <option value="">Choose an operation…</option>
          {(Object.entries(OPERATION_KIND_LABELS) as [ScenarioOperationKind, string][]).map(
            ([k, kindLabel]) => (
              <option
                key={k}
                value={k}
                disabled={hasSolveOp && (k === 'solveSpend' || k === 'solveSaving')}
              >
                {kindLabel}
              </option>
            ),
          )}
        </select>
      </Field>

      {hasSolveOp && (
        <p className="mb-3 text-sm text-muted">
          Only one of solve safe spend / solve required saving may be included per scenario — remove
          the existing one first.
        </p>
      )}

      {(kind === 'delay' || kind === 'change') && (
        <Field label="Receivable / payable" htmlFor="sim-op-rp">
          <select
            id="sim-op-rp"
            className={selectClass}
            value={rpId}
            onChange={(e) => {
              setRpId(e.target.value);
            }}
          >
            <option value="">Select a receivable/payable…</option>
            {rps.map((r) => (
              <option key={r.id} value={r.id}>
                {r.party} — {formatMoney(fromMinor(r.currency, r.amountMinor - r.settledMinor))}{' '}
                remaining ({r.reason})
              </option>
            ))}
          </select>
        </Field>
      )}
      {kind === 'delay' && (
        <Field label="Delay by (days)" htmlFor="sim-op-days">
          <input
            id="sim-op-days"
            type="number"
            min={1}
            className={inputClass}
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
            }}
          />
        </Field>
      )}
      {kind === 'change' && (
        <Field label="New amount" htmlFor="sim-op-new-amount">
          <AmountField
            id="sim-op-new-amount"
            currency={currency}
            value={newAmount}
            onChange={setNewAmount}
          />
        </Field>
      )}

      {kind === 'add' && (
        <>
          <div role="radiogroup" aria-label="Expense or income" className="mb-3 flex gap-2">
            {(['expense', 'income'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={cashflowKind === k}
                onClick={() => {
                  setCashflowKind(k);
                }}
                className={`rounded-[var(--radius-control)] border px-3 py-1.5 text-sm capitalize ${
                  cashflowKind === k
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border text-ink hover:bg-bg'
                }`}
              >
                {k}
              </button>
            ))}
          </div>
          <Field label="Amount" htmlFor="sim-op-add-amount">
            <AmountField
              id="sim-op-add-amount"
              currency={currency}
              value={addAmount}
              onChange={setAddAmount}
            />
          </Field>
          <Field label="Date" htmlFor="sim-op-add-date">
            <input
              id="sim-op-add-date"
              type="date"
              className={inputClass}
              value={at}
              onChange={(e) => {
                setAt(e.target.value);
              }}
            />
          </Field>
          <Field label="Label" htmlFor="sim-op-add-label">
            <input
              id="sim-op-add-label"
              className={inputClass}
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
              }}
            />
          </Field>
        </>
      )}

      {kind === 'payAll' && (
        <p className="mb-3 text-sm text-muted">
          Every open payable is moved to today (paid immediately).
        </p>
      )}
      {kind === 'solveSpend' && (
        <p className="mb-3 text-sm text-muted">
          Solves the highest safe daily spend that never breaches the reserve floor.
        </p>
      )}
      {kind === 'solveSaving' && (
        <Field label="Goal" htmlFor="sim-op-goal">
          <select
            id="sim-op-goal"
            className={selectClass}
            value={goalId}
            onChange={(e) => {
              setGoalId(e.target.value);
            }}
          >
            <option value="">Select a goal…</option>
            {goals.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
      {kind !== '' && (
        <button type="button" className={primaryButtonInlineClass} onClick={handleAdd}>
          Add operation
        </button>
      )}
    </div>
  );
}

function SavedScenarioRow({
  scenario,
  onLoad,
}: {
  scenario: SavedScenario;
  onLoad: (config: Record<string, unknown>) => void;
}): React.JSX.Element {
  const deleteScenario = useDeleteScenario(scenario.id);
  return (
    <li className="flex items-center justify-between gap-2 border-b border-border py-1.5 last:border-0">
      <span className="truncate text-sm text-ink">{scenario.name}</span>
      <div className="flex shrink-0 gap-3">
        <button
          type="button"
          aria-label={`Load ${scenario.name}`}
          className="text-sm text-primary hover:underline"
          onClick={() => {
            onLoad(scenario.config);
          }}
        >
          Load
        </button>
        <button
          type="button"
          aria-label={`Delete ${scenario.name}`}
          className="text-xs text-muted hover:text-danger"
          disabled={deleteScenario.isPending}
          onClick={() => {
            deleteScenario.mutate();
          }}
        >
          Delete
        </button>
      </div>
    </li>
  );
}

export function SimulatorPage(): React.JSX.Element {
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [windowDays, setWindowDays] = useState(30);
  const [operations, setOperations] = useState<ScenarioOperation[]>([]);
  const [slots, setSlots] = useState<ScenarioSlot[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  const [saveName, setSaveName] = useState('');

  const { data: rps } = useRps({ status: 'open' });
  const { data: goals } = useGoals({ status: 'active' });
  const runScenario = useRunScenario();
  const savedScenarios = useSavedScenarios();
  const saveScenario = useSaveScenario();

  const eligibleRps = useMemo(
    () => (rps ?? []).filter((r) => r.currency === currency),
    [rps, currency],
  );
  const activeGoals = useMemo(() => goals ?? [], [goals]);
  const rpPartyById = useMemo(() => new Map((rps ?? []).map((r) => [r.id, r.party])), [rps]);
  const goalNameById = useMemo(() => new Map((goals ?? []).map((g) => [g.id, g.name])), [goals]);

  const hasSolveOp = operations.some((op) => op.kind === 'solveSpend' || op.kind === 'solveSaving');
  const chartOption = useMemo(() => buildSimulatorChartOption(slots), [slots]);

  const removeOperation = (index: number): void => {
    setOperations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRun = (): void => {
    if (slots.length >= 3) return;
    setRunError(null);
    const solveOp = operations.find((op) => op.kind === 'solveSpend' || op.kind === 'solveSaving');
    runScenario.mutate(
      { currency, windowDays, operations },
      {
        onSuccess: (result) => {
          setSlots((prev) => {
            if (prev.length >= 3) return prev;
            return [
              ...prev,
              {
                key: crypto.randomUUID(),
                label: `Scenario ${String(prev.length + 1)}`,
                result,
                ...(solveOp !== undefined ? { solveKind: solveOp.kind } : {}),
              },
            ];
          });
        },
        onError: (e) => {
          setRunError(e.message);
        },
      },
    );
  };

  const removeSlot = (key: string): void => {
    setSlots((prev) => prev.filter((s) => s.key !== key));
  };

  const handleSave = (): void => {
    if (saveName.trim() === '') return;
    saveScenario.mutate(
      { name: saveName.trim(), config: { currency, windowDays, operations } },
      {
        onSuccess: () => {
          setSaveName('');
        },
      },
    );
  };

  const handleLoad = (config: Record<string, unknown>): void => {
    const c = config as {
      currency?: CurrencyCode;
      windowDays?: number;
      operations?: ScenarioOperation[];
    };
    if (c.currency !== undefined) setCurrency(c.currency);
    if (c.windowDays !== undefined) setWindowDays(c.windowDays);
    if (c.operations !== undefined) setOperations(c.operations);
  };

  return (
    <div>
      {/* SM-2: always visible, never behind a loading/data guard — the sandbox promise holds
          even before anything has loaded or run. */}
      <div
        role="status"
        className="mb-4 rounded-[var(--radius-card)] border border-primary/30 bg-primary/5 p-3 text-sm font-medium text-ink"
      >
        Sandbox — nothing here touches real records.
      </div>

      <h1 className="mb-4 text-xl font-semibold text-ink">Simulator</h1>

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Currency</span>
          <select
            aria-label="Currency"
            className={`${selectClass} w-auto`}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value as CurrencyCode);
            }}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Window (days)</span>
          <input
            aria-label="Window (days)"
            type="number"
            min={1}
            className={`${inputClass} w-28`}
            value={windowDays}
            onChange={(e) => {
              setWindowDays(Number(e.target.value));
            }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="space-y-4 lg:w-96 lg:shrink-0">
          <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
            <p className="mb-3 text-sm font-medium text-ink">Operations in this scenario</p>
            {operations.length === 0 ? (
              <p className="mb-3 text-sm text-muted">
                No operations added yet — the projection uses only existing open RPs and recurring
                items.
              </p>
            ) : (
              <ul className="mb-3 space-y-2">
                {operations.map((op, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-2 rounded-[var(--radius-control)] border border-border px-3 py-2 text-sm"
                  >
                    <span className="text-ink">
                      {describeOperation(op, currency, rpPartyById, goalNameById)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove operation ${String(i + 1)}`}
                      className="text-muted hover:text-danger"
                      onClick={() => {
                        removeOperation(i);
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className={primaryButtonInlineClass}
                disabled={runScenario.isPending || slots.length >= 3}
                onClick={handleRun}
              >
                Run scenario
              </button>
              {slots.length >= 3 && (
                <p className="text-sm text-muted">
                  Comparison is full (3 scenarios) — remove one to run another.
                </p>
              )}
            </div>
            {runError !== null && <p className="mt-2 text-sm text-danger">{runError}</p>}
          </div>

          <AddOperationForm
            currency={currency}
            rps={eligibleRps}
            goals={activeGoals}
            hasSolveOp={hasSolveOp}
            onAdd={(op) => {
              setOperations((prev) => [...prev, op]);
            }}
          />

          <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
            <p className="mb-3 text-sm font-medium text-ink">Saved scenarios</p>
            <div className="mb-2 flex gap-2">
              <input
                aria-label="Scenario name"
                className={`${inputClass} flex-1`}
                placeholder="Name this scenario"
                value={saveName}
                onChange={(e) => {
                  setSaveName(e.target.value);
                }}
              />
              <button
                type="button"
                className={ghostButtonInlineClass}
                disabled={saveScenario.isPending || saveName.trim() === ''}
                onClick={handleSave}
              >
                Save this scenario
              </button>
            </div>
            {(savedScenarios.data ?? []).length === 0 ? (
              <p className="text-sm text-muted">No saved scenarios yet.</p>
            ) : (
              <ul>
                {(savedScenarios.data ?? []).map((s) => (
                  <SavedScenarioRow key={s.id} scenario={s} onLoad={handleLoad} />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {slots.length === 0 ? (
            <EmptyState
              icon={FlaskConical}
              message="Run a scenario to see its projected balance here."
            />
          ) : (
            <>
              <EChart option={chartOption} ariaLabel="Scenario projected balance chart" />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted">
                      <th className="py-2 pr-4 font-medium">Scenario</th>
                      <th className="py-2 pr-4 font-medium">Ending balance</th>
                      <th className="py-2 pr-4 font-medium">Reserve floor</th>
                      <th className="py-2 pr-4 font-medium">Breach days</th>
                      <th className="py-2 pr-4 font-medium">Solved</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map((slot) => {
                      const lastPoint = slot.result.points[slot.result.points.length - 1];
                      return (
                        <tr key={slot.key} className="border-b border-border last:border-0">
                          <td className="py-2 pr-4 text-ink">{slot.label}</td>
                          <td className="py-2 pr-4">
                            <Amount
                              currency={slot.result.currency}
                              minorUnits={lastPoint?.balanceMinor ?? 0}
                            />
                          </td>
                          <td className="py-2 pr-4">
                            <Amount
                              currency={slot.result.currency}
                              minorUnits={slot.result.reserveFloorMinor}
                            />
                          </td>
                          <td className="py-2 pr-4 text-ink">{slot.result.breaches.length}</td>
                          <td className="py-2 pr-4 text-ink">
                            {slot.result.solvedAmountMinor !== undefined ? (
                              <>
                                {slot.solveKind === 'solveSaving'
                                  ? 'Required monthly saving: '
                                  : 'Safe daily spend: '}
                                <Amount
                                  currency={slot.result.currency}
                                  minorUnits={slot.result.solvedAmountMinor}
                                />
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="py-2">
                            <button
                              type="button"
                              aria-label={`Remove ${slot.label} from comparison`}
                              className="text-xs text-muted hover:text-danger"
                              onClick={() => {
                                removeSlot(slot.key);
                              }}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
